import React, { useEffect, useState } from 'react';
import { getCurrentUser } from '../../Authentication/auth';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const COUNTRY_OPTIONS = [
  { code: 'IN', name: 'India', currency: 'INR', timezone: 'Asia/Kolkata' },
  { code: 'US', name: 'United States', currency: 'USD', timezone: 'America/New_York' },
  { code: 'GB', name: 'United Kingdom', currency: 'GBP', timezone: 'Europe/London' },
  { code: 'AE', name: 'United Arab Emirates', currency: 'AED', timezone: 'Asia/Dubai' },
  { code: 'SG', name: 'Singapore', currency: 'SGD', timezone: 'Asia/Singapore' },
  { code: 'AU', name: 'Australia', currency: 'AUD', timezone: 'Australia/Sydney' },
  { code: 'CA', name: 'Canada', currency: 'CAD', timezone: 'America/Toronto' },
];
const CURRENCY_OPTIONS = ['INR', 'USD', 'GBP', 'EUR', 'AED', 'SGD', 'AUD', 'CAD'];
const TIMEZONE_OPTIONS = [
  'Asia/Kolkata',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'America/Toronto',
  'Europe/London',
  'Europe/Paris',
  'Asia/Dubai',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Sydney',
  'UTC',
];

const HOTEL_POLICY_DEFAULTS = {
  title: 'Hotel Rules',
  currency: 'INR',
  checkin_from_time: '14:00',
  checkin_to_time: '23:59',
  checkout_from_time: '06:00',
  checkout_to_time: '12:00',
  max_count_of_guests: 2,
  is_adults_only: false,
  children_max_age: 12,
  infant_max_age: 5,
  internet_access_type: 'wifi',
  internet_access_coverage: 'entire_property',
  internet_access_cost: '',
  parking_type: 'on_site',
  parking_reservation: 'not_needed',
  parking_is_private: true,
  parking_cost: '',
  pets_policy: 'not_allowed',
  pets_non_refundable_fee: '',
  pets_refundable_deposit: '',
  smoking_policy: 'no_smoking',
  self_checkin_checkout: false,
  enhanced_cleaning_practices: false,
  cleaning_practices_description: '',
  partner_hygiene_link: '',
  extra_bed_policy: '',
  cx_hotel_policy_id: '',
};

const HOTEL_POLICY_SECTIONS = [
  {
    title: 'General & Guest Rules',
    fields: [
      { name: 'title', label: 'Policy Title', type: 'text', required: true },
      {
        name: 'currency',
        label: 'Currency',
        type: 'select',
        options: ['INR', 'USD', 'GBP', 'EUR', 'AED', 'SGD', 'AUD', 'CAD'],
      },
      {
        name: 'max_count_of_guests',
        label: 'Maximum Guests',
        type: 'number',
        min: 1,
        required: true,
      },
      { name: 'is_adults_only', label: 'Adults Only', type: 'checkbox' },
      { name: 'children_max_age', label: 'Maximum Children Age', type: 'number', min: 0, max: 18 },
      { name: 'infant_max_age', label: 'Maximum Infant Age', type: 'number', min: 0, max: 5 },
    ],
  },
  {
    title: 'Check-in & Check-out',
    fields: [
      { name: 'checkin_from_time', label: 'Check-in From', type: 'time', required: true },
      { name: 'checkin_to_time', label: 'Check-in Until', type: 'time', required: true },
      { name: 'checkout_from_time', label: 'Check-out From', type: 'time', required: true },
      { name: 'checkout_to_time', label: 'Check-out Until', type: 'time', required: true },
      {
        name: 'self_checkin_checkout',
        label: 'Self Check-in / Check-out Available',
        type: 'checkbox',
      },
    ],
  },
  {
    title: 'Internet Access',
    fields: [
      { name: 'internet_access_type', label: 'Internet Type', type: 'text' },
      { name: 'internet_access_coverage', label: 'Internet Coverage', type: 'text' },
      {
        name: 'internet_access_cost',
        label: 'Internet Cost',
        type: 'number',
        min: 0,
        step: '0.01',
      },
    ],
  },
  {
    title: 'Parking',
    fields: [
      { name: 'parking_type', label: 'Parking Type', type: 'text' },
      { name: 'parking_reservation', label: 'Parking Reservation', type: 'text' },
      { name: 'parking_is_private', label: 'Private Parking', type: 'checkbox' },
      { name: 'parking_cost', label: 'Parking Cost', type: 'number', min: 0, step: '0.01' },
    ],
  },
  {
    title: 'Pets, Smoking & Extra Beds',
    fields: [
      { name: 'pets_policy', label: 'Pets Policy', type: 'text' },
      {
        name: 'pets_non_refundable_fee',
        label: 'Pets Non-refundable Fee',
        type: 'number',
        min: 0,
        step: '0.01',
      },
      {
        name: 'pets_refundable_deposit',
        label: 'Pets Refundable Deposit',
        type: 'number',
        min: 0,
        step: '0.01',
      },
      { name: 'smoking_policy', label: 'Smoking Policy', type: 'text' },
      { name: 'extra_bed_policy', label: 'Extra Bed Policy', type: 'text' },
    ],
  },
  {
    title: 'Cleaning & Hygiene',
    fields: [
      {
        name: 'enhanced_cleaning_practices',
        label: 'Enhanced Cleaning Practices',
        type: 'checkbox',
      },
      {
        name: 'cleaning_practices_description',
        label: 'Cleaning Practices Description',
        type: 'textarea',
      },
      { name: 'partner_hygiene_link', label: 'Partner Hygiene Link', type: 'url' },
      { name: 'cx_hotel_policy_id', label: 'External Hotel Policy ID', type: 'text' },
    ],
  },
];

// Custom Modal Component
const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, loading }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="mt-2 text-sm text-gray-600">{message}</p>
        </div>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center">
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Deleting...
              </span>
            ) : (
              'Delete'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const ReadOnly = ({ label, value }) => (
  <div className="space-y-1">
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    <input
      value={value ?? ''}
      readOnly
      className="w-full px-4 py-2 border border-gray-200 bg-gray-50 text-gray-500 rounded-lg"
    />
  </div>
);

const PropertySettings = ({ property: propProp, onUpdate }) => {
  const [property, setProperty] = useState(propProp || null);
  const [formData, setFormData] = useState({
    propertyname: '',
    city: '',
    name: '',
    location: '',
    address: '',
    phone: '',
    email: '',
    maxGuestsPerRoom: 2,
    checkInTime: '14:00',
    checkOutTime: '12:00',
    cancellationPolicy: 'Free cancellation up to 24 hours before check-in',
    petPolicy: 'Not allowed',
    smokingPolicy: 'Not allowed',
    extraBedPolicy: 'Available with additional charge',
    hasWifi: true,
    hasPool: false,
    hasGym: false,
    hasRestaurant: true,
    hasParking: true,
    hasSpa: false,
    gstnumber: '',
    pan_number: '',
    fssai_number: '',
    cin_number: '',
    state_code: '',
    bank_account_name: '',
    bank_account_no: '',
    bank_ifsc: '',
    currency: 'INR',
    country: 'IN',
    time_zone: 'Asia/Kolkata',
    checkin_time: '14:00',
    checkout_time: '12:00',
  });

  const [logoFile, setLogoFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [homepageFile, setHomepageFile] = useState(null);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [hotelPolicy, setHotelPolicy] = useState(null);
  const [hotelPolicyForm, setHotelPolicyForm] = useState(HOTEL_POLICY_DEFAULTS);
  const [hotelPolicyLoading, setHotelPolicyLoading] = useState(false);
  const [hotelPolicySaving, setHotelPolicySaving] = useState(false);
  const [hotelPolicyDeleting, setHotelPolicyDeleting] = useState(false);
  const [errors, setErrors] = useState({});
  const [isDeleting, setIsDeleting] = useState({
    logo: false,
    homepageImage: false,
    video: false,
    all: false,
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfig, setDeleteConfig] = useState({
    type: '',
    title: '',
    message: '',
    onConfirm: () => {},
  });

  // ── Gallery state (INLINE, no new component) ──
  const [galleryItems, setGalleryItems] = useState([]);
  const [galleryCategories, setGalleryCategories] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryFilterType, setGalleryFilterType] = useState('');
  const [galleryFilterCategory, setGalleryFilterCategory] = useState('');
  const [galleryMediaType, setGalleryMediaType] = useState('image');
  const [galleryCategory, setGalleryCategory] = useState('general');
  const [galleryFile, setGalleryFile] = useState(null);
  const [galleryTitle, setGalleryTitle] = useState('');
  const [galleryDescription, setGalleryDescription] = useState('');
  const [galleryOrder, setGalleryOrder] = useState(0);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    fetchProperty();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (activeTab === 'media' && property?.propertyid) loadGallery();
    // eslint-disable-next-line
  }, [activeTab, galleryFilterType, galleryFilterCategory]);

  useEffect(() => {
    if (activeTab === 'rules' && property?.propertyid) {
      loadHotelPolicy();
    }
    // eslint-disable-next-line
  }, [activeTab, property?.propertyid]);

  const loadHotelPolicy = async () => {
    if (!property?.propertyid) return;
    setHotelPolicyLoading(true);
    try {
      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/properties/${property.propertyid}/hotel-policy/`
      );

      if (response.status === 404) {
        setHotelPolicy(null);
        setHotelPolicyForm({
          ...HOTEL_POLICY_DEFAULTS,
          property_id: Number(property.propertyid),
        });
        return;
      }
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to load hotel policy');
      }

      const data = await response.json();
      setHotelPolicy(data);
      setHotelPolicyForm({
        ...HOTEL_POLICY_DEFAULTS,
        ...data,
        property_id: Number(property.propertyid),
        checkin_from_time: data.checkin_from_time?.slice(0, 5) || '',
        checkin_to_time: data.checkin_to_time?.slice(0, 5) || '',
        checkout_from_time: data.checkout_from_time?.slice(0, 5) || '',
        checkout_to_time: data.checkout_to_time?.slice(0, 5) || '',
        internet_access_cost: data.internet_access_cost ?? '',
        parking_cost: data.parking_cost ?? '',
        pets_non_refundable_fee: data.pets_non_refundable_fee ?? '',
        pets_refundable_deposit: data.pets_refundable_deposit ?? '',
      });
    } catch (error) {
      console.error('Failed to load hotel policy:', error);
      toast.error(error.message || 'Failed to load hotel rules');
    } finally {
      setHotelPolicyLoading(false);
    }
  };

  const handleHotelPolicyChange = (event) => {
    const { name, value, type, checked } = event.target;
    setHotelPolicyForm((previous) => ({
      ...previous,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleHotelPolicySave = async () => {
    if (!property?.propertyid) {
      toast.error('Property information is not available');
      return;
    }

    const requiredFields = [
      'title',
      'checkin_from_time',
      'checkin_to_time',
      'checkout_from_time',
      'checkout_to_time',
      'max_count_of_guests',
    ];
    const missingField = requiredFields.find(
      (field) =>
        hotelPolicyForm[field] === '' ||
        hotelPolicyForm[field] === null ||
        hotelPolicyForm[field] === undefined
    );
    if (missingField) {
      toast.error(`${missingField.replaceAll('_', ' ')} is required`);
      return;
    }

    const numericFields = [
      'max_count_of_guests',
      'children_max_age',
      'infant_max_age',
      'internet_access_cost',
      'parking_cost',
      'pets_non_refundable_fee',
      'pets_refundable_deposit',
    ];
    const payload = {
      ...hotelPolicyForm,
      property_id: Number(property.propertyid),
    };
    numericFields.forEach((field) => {
      if (payload[field] === '' || payload[field] === null || payload[field] === undefined) {
        payload[field] = null;
      } else {
        payload[field] = Number(payload[field]);
      }
    });

    setHotelPolicySaving(true);
    try {
      const response = await fetch(
        hotelPolicy
          ? `${BQ_BASE_URL}/bq/api/property-hotel-policies/${hotelPolicy.id}`
          : `${BQ_BASE_URL}/bq/api/property-hotel-policies/`,
        {
          method: hotelPolicy ? 'PATCH' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            accept: 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        let errorMessage = 'Failed to save hotel policy';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch {
          // Keep the fallback message when the response is not JSON.
        }
        throw new Error(errorMessage);
      }

      const savedPolicy = await response.json();
      setHotelPolicy(savedPolicy);
      setHotelPolicyForm((previous) => ({
        ...previous,
        ...savedPolicy,
        checkin_from_time: savedPolicy.checkin_from_time?.slice(0, 5) || '',
        checkin_to_time: savedPolicy.checkin_to_time?.slice(0, 5) || '',
        checkout_from_time: savedPolicy.checkout_from_time?.slice(0, 5) || '',
        checkout_to_time: savedPolicy.checkout_to_time?.slice(0, 5) || '',
      }));
      toast.success(
        hotelPolicy ? 'Hotel rules updated successfully' : 'Hotel rules created successfully'
      );
    } catch (error) {
      console.error('Failed to save hotel policy:', error);
      toast.error(error.message || 'Failed to save hotel rules');
    } finally {
      setHotelPolicySaving(false);
    }
  };

  const handleHotelPolicyDelete = async () => {
    if (!hotelPolicy?.id) return;
    if (!window.confirm('Are you sure you want to delete this hotel policy?')) return;

    setHotelPolicyDeleting(true);
    try {
      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/property-hotel-policies/${hotelPolicy.id}`,
        { method: 'DELETE' }
      );
      if (!response.ok) {
        let errorMessage = 'Failed to delete hotel policy';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch {
          // Keep the fallback message.
        }
        throw new Error(errorMessage);
      }

      setHotelPolicy(null);
      setHotelPolicyForm({
        ...HOTEL_POLICY_DEFAULTS,
        property_id: Number(property.propertyid),
      });
      toast.success('Hotel rules deleted successfully');
    } catch (error) {
      console.error('Failed to delete hotel policy:', error);
      toast.error(error.message || 'Failed to delete hotel rules');
    } finally {
      setHotelPolicyDeleting(false);
    }
  };

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validateIndianPhone = (phone) => /^\d{10}$/.test(phone);

  const validateField = (name, value) => {
    const newErrors = { ...errors };
    switch (name) {
      case 'email':
        if (value && !validateEmail(value)) newErrors.email = 'Please enter a valid email address';
        else delete newErrors.email;
        break;
      case 'phone':
        if (value) {
          if (!validateIndianPhone(value))
            newErrors.phone = 'Please enter a valid 10-digit mobile number';
          else delete newErrors.phone;
        } else delete newErrors.phone;
        break;
      case 'propertyname':
        if (!value?.trim()) newErrors.propertyname = 'Property name is required';
        else delete newErrors.propertyname;
        break;
      case 'city':
        if (!value?.trim()) newErrors.city = 'City is required';
        else delete newErrors.city;
        break;
      case 'name':
        if (!value?.trim()) newErrors.name = 'Hotel name is required';
        else delete newErrors.name;
        break;
      default:
        break;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const fetchProperty = async () => {
    try {
      const currentUser = await getCurrentUser();
      if (!currentUser) {
        toast.error('Failed to fetch user data. Please log in again.');
        return;
      }
      const res = await fetch(`${BQ_BASE_URL}/bq/api/properties/${currentUser.property_id}`);
      const data = await res.json();
      setProperty(data);

      const formattedData = {
        ...data,
        checkin_time: data.checkin_time
          ? data.checkin_time.split('T')[1]?.substring(0, 5) || '14:00'
          : '14:00',
        checkout_time: data.checkout_time
          ? data.checkout_time.split('T')[1]?.substring(0, 5) || '12:00'
          : '12:00',
      };
      setFormData((prev) => ({ ...prev, ...formattedData }));
    } catch (err) {
      console.error('Failed to fetch property:', err);
      toast.error('Failed to fetch property details');
    }
  };

  const loadGallery = async () => {
    setGalleryLoading(true);
    try {
      if (galleryCategories.length === 0) {
        try {
          const catsRes = await fetch(`${BQ_BASE_URL}/bq/api/properties/media/categories`);
          const cats = await catsRes.json();
          setGalleryCategories(cats.categories || []);
        } catch {
          /* ignore */
        }
      }
      const params = new URLSearchParams();
      if (galleryFilterType) params.append('media_type', galleryFilterType);
      if (galleryFilterCategory) params.append('category', galleryFilterCategory);
      const qs = params.toString();
      const res = await fetch(
        `${BQ_BASE_URL}/bq/api/properties/${property.propertyid}/media${qs ? `?${qs}` : ''}`
      );
      if (!res.ok) throw new Error('Failed to fetch media');
      const data = await res.json();
      setGalleryItems(data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setGalleryLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    setFormData((prev) => ({ ...prev, [name]: newValue }));
    if (name === 'email' || name === 'phone') validateField(name, newValue);
  };

  const handleCountryChange = (e) => {
    const code = e.target.value;
    const found = COUNTRY_OPTIONS.find((c) => c.code === code);
    setFormData((prev) => ({
      ...prev,
      country: code,
      currency: found?.currency || prev.currency,
      time_zone: found?.timezone || prev.time_zone,
    }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (name === 'email' || name === 'phone') validateField(name, value);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage('');

    const requiredFields = ['propertyname', 'city', 'name', 'email', 'phone'];
    let hasErrors = false;
    const newErrors = {};

    requiredFields.forEach((field) => {
      if (!formData[field]?.trim()) {
        newErrors[field] = `${
          field === 'propertyname'
            ? 'Property name'
            : field === 'city'
              ? 'City'
              : field === 'name'
                ? 'Hotel name'
                : field
        } is required`;
        hasErrors = true;
      }
    });

    if (formData.email && !validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      hasErrors = true;
    }
    if (formData.phone && !validateIndianPhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid 10-digit mobile number';
      hasErrors = true;
    }
    if (hasErrors) {
      setErrors(newErrors);
      setIsSubmitting(false);
      toast.error('Please fix the validation errors before submitting.');
      return;
    }

    const form = new FormData();
    const allFields = [
      'propertyname',
      'city',
      'name',
      'location',
      'address',
      'phone',
      'email',
      'gstnumber',
      'pan_number',
      'fssai_number',
      'cin_number',
      'state_code',
      'bank_account_name',
      'bank_account_no',
      'bank_ifsc',
      'currency',
      'country',
      'time_zone',
      'checkin_time',
      'checkout_time',
    ];

    allFields.forEach((key) => {
      let value = formData[key];
      if (value === null || value === undefined) value = '';
      form.append(key, value.toString());
    });

    if (logoFile) form.append('logo_file', logoFile);
    if (homepageFile) form.append('homepage_file', homepageFile);
    if (videoFile) form.append('video_file', videoFile);

    try {
      const currentUser = await getCurrentUser();
      if (!currentUser) {
        toast.error('Failed to fetch user data. Please log in again.');
        setIsSubmitting(false);
        return;
      }

      const res = await fetch(`${BQ_BASE_URL}/bq/api/properties/${currentUser.property_id}`, {
        method: 'PUT',
        body: form,
      });

      if (!res.ok) {
        const errorData = await res.text();
        console.error('API Error:', errorData);
        throw new Error('Failed to update property');
      }

      const updated = await res.json();
      setProperty(updated);
      setFormData((prev) => ({ ...prev, ...updated }));
      setLogoFile(null);
      setHomepageFile(null);
      setVideoFile(null);
      setMessage('✅ Property updated successfully!');
      toast.success('Property updated successfully!');
      onUpdate?.(updated);
    } catch (err) {
      console.error(err);
      setMessage('❌ Failed to update property');
      toast.error('Failed to update property');
    } finally {
      setIsSubmitting(false);
    }
  };

  const showDeleteConfirmation = (type, title, message, onConfirmCallback) => {
    setDeleteConfig({
      type,
      title,
      message,
      onConfirm: () => {
        setShowDeleteModal(false);
        onConfirmCallback();
      },
    });
    setShowDeleteModal(true);
  };

  const deleteMedia = async (endpoint, mediaType) => {
    try {
      const currentUser = await getCurrentUser();
      if (!currentUser || !currentUser.property_id) {
        toast.error('Failed to fetch user data. Please log in again.');
        return null;
      }
      setIsDeleting((prev) => ({ ...prev, [mediaType]: true }));
      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/properties/${currentUser.property_id}${endpoint}`,
        { method: 'DELETE', headers: { 'Content-Type': 'application/json' } }
      );
      if (!response.ok) throw new Error(`Failed to delete ${mediaType}`);
      const result = await response.json();

      setProperty((prev) => ({
        ...prev,
        logo_url: mediaType === 'logo' ? null : prev.logo_url,
        homepage_url: mediaType === 'homepage' ? null : prev.homepage_url,
        homepage_video_url: mediaType === 'homepage_video' ? null : prev.homepage_video_url,
      }));
      setFormData((prev) => ({
        ...prev,
        logo_url: mediaType === 'logo' ? null : prev.logo_url,
        homepage_url: mediaType === 'homepage' ? null : prev.homepage_url,
        homepage_video_url: mediaType === 'homepage_video' ? null : prev.homepage_video_url,
      }));

      if (mediaType === 'logo') setLogoFile(null);
      if (mediaType === 'homepage') setHomepageFile(null);
      if (mediaType === 'homepage_video') setVideoFile(null);
      return result;
    } catch (error) {
      console.error(`Error deleting ${mediaType}:`, error);
      toast.error(`Failed to delete ${mediaType}: ${error.message}`);
      return null;
    } finally {
      setIsDeleting((prev) => ({ ...prev, [mediaType]: false }));
    }
  };

  const handleDeleteLogo = () => {
    showDeleteConfirmation(
      'logo',
      'Delete Logo',
      'Are you sure you want to delete the logo? This action cannot be undone.',
      async () => {
        const toastId = toast.loading('Deleting logo...');
        const result = await deleteMedia('/logo', 'logo');
        toast.update(toastId, {
          render: result ? 'Logo deleted successfully!' : 'Failed to delete logo',
          type: result ? 'success' : 'error',
          isLoading: false,
          autoClose: 3000,
        });
      }
    );
  };

  const handleDeleteHomepageImage = () => {
    showDeleteConfirmation(
      'homepageImage',
      'Delete Homepage Image',
      'Are you sure you want to delete the homepage image? This action cannot be undone.',
      async () => {
        const toastId = toast.loading('Deleting homepage image...');
        const result = await deleteMedia('/homepage-image', 'homepage');
        toast.update(toastId, {
          render: result
            ? 'Homepage image deleted successfully!'
            : 'Failed to delete homepage image',
          type: result ? 'success' : 'error',
          isLoading: false,
          autoClose: 3000,
        });
      }
    );
  };

  const handleDeleteVideo = () => {
    showDeleteConfirmation(
      'video',
      'Delete Property Video',
      'Are you sure you want to delete the property video? This action cannot be undone.',
      async () => {
        const toastId = toast.loading('Deleting video...');
        const result = await deleteMedia('/homepage-video', 'homepage_video');
        toast.update(toastId, {
          render: result ? 'Video deleted successfully!' : 'Failed to delete video',
          type: result ? 'success' : 'error',
          isLoading: false,
          autoClose: 3000,
        });
      }
    );
  };

  const handleDeleteAllMedia = () => {
    showDeleteConfirmation(
      'all',
      'Delete All Media',
      'Are you sure you want to delete ALL media (logo, homepage image, and video)? This action cannot be undone.',
      async () => {
        const toastId = toast.loading('Deleting all media...');
        try {
          const currentUser = await getCurrentUser();
          if (!currentUser || !currentUser.property_id) {
            toast.error('Failed to fetch user data. Please log in again.');
            return;
          }
          setIsDeleting((prev) => ({ ...prev, all: true }));
          const response = await fetch(
            `${BQ_BASE_URL}/bq/api/properties/${currentUser.property_id}/all-media`,
            { method: 'DELETE', headers: { 'Content-Type': 'application/json' } }
          );
          if (!response.ok) throw new Error('Failed to delete all media');
          const result = await response.json();
          setProperty((prev) => ({
            ...prev,
            logo_url: null,
            homepage_url: null,
            homepage_video_url: null,
          }));
          setFormData((prev) => ({
            ...prev,
            logo_url: null,
            homepage_url: null,
            homepage_video_url: null,
          }));
          setLogoFile(null);
          setHomepageFile(null);
          setVideoFile(null);
          toast.update(toastId, {
            render: 'All media deleted successfully!',
            type: 'success',
            isLoading: false,
            autoClose: 3000,
          });
          return result;
        } catch (error) {
          console.error('Error deleting all media:', error);
          toast.update(toastId, {
            render: 'Failed to delete all media',
            type: 'error',
            isLoading: false,
            autoClose: 3000,
          });
          return null;
        } finally {
          setIsDeleting((prev) => ({ ...prev, all: false }));
        }
      }
    );
  };

  // ── Gallery handlers (INLINE) ──
  const handleGalleryUpload = async () => {
    if (!galleryFile) {
      toast.error('Select a file');
      return;
    }
    setGalleryUploading(true);
    try {
      const form = new FormData();
      form.append('media_type', galleryMediaType);
      form.append('category', galleryCategory);
      form.append('file', galleryFile);
      if (galleryTitle) form.append('title', galleryTitle);
      if (galleryDescription) form.append('description', galleryDescription);
      form.append('display_order', String(galleryOrder));

      const res = await fetch(`${BQ_BASE_URL}/bq/api/properties/${property.propertyid}/media`, {
        method: 'POST',
        body: form,
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'Failed to upload media');
      }
      toast.success('Media uploaded');
      setGalleryFile(null);
      setGalleryTitle('');
      setGalleryDescription('');
      setGalleryOrder(0);
      await loadGallery();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setGalleryUploading(false);
    }
  };

  const handleGalleryToggleActive = async (item) => {
    try {
      const form = new FormData();
      form.append('is_active', String(!item.is_active));
      const res = await fetch(
        `${BQ_BASE_URL}/bq/api/properties/${property.propertyid}/media/${item.id}`,
        {
          method: 'PUT',
          body: form,
        }
      );
      if (!res.ok) throw new Error('Failed to update');
      await loadGallery();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleGalleryDelete = async (item) => {
    if (!window.confirm('Delete this media item?')) return;
    try {
      const res = await fetch(
        `${BQ_BASE_URL}/bq/api/properties/${property.propertyid}/media/${item.id}`,
        {
          method: 'DELETE',
        }
      );
      if (!res.ok) throw new Error('Failed to delete');
      toast.success('Deleted');
      await loadGallery();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleGalleryBulkDelete = async () => {
    if (!window.confirm('Delete ALL filtered media?')) return;
    try {
      const params = new URLSearchParams();
      if (galleryFilterType) params.append('media_type', galleryFilterType);
      if (galleryFilterCategory) params.append('category', galleryFilterCategory);
      const qs = params.toString();
      const res = await fetch(
        `${BQ_BASE_URL}/bq/api/properties/${property.propertyid}/media${qs ? `?${qs}` : ''}`,
        { method: 'DELETE' }
      );
      if (!res.ok) throw new Error('Failed to bulk delete');
      const data = await res.json();
      toast.success(`Deleted ${data.deleted_count} items`);
      await loadGallery();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleGalleryUpdate = async () => {
    if (!editingItem) return;
    try {
      const form = new FormData();
      form.append('category', editingItem.category);
      if (editingItem.title) form.append('title', editingItem.title);
      if (editingItem.description) form.append('description', editingItem.description);
      form.append('display_order', String(editingItem.display_order ?? 0));
      form.append('is_active', String(editingItem.is_active));

      const res = await fetch(
        `${BQ_BASE_URL}/bq/api/properties/${property.propertyid}/media/${editingItem.id}`,
        {
          method: 'PUT',
          body: form,
        }
      );
      if (!res.ok) throw new Error('Failed to update');
      toast.success('Updated');
      setEditingItem(null);
      await loadGallery();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const hasValidationErrors = () => {
    const requiredFields = ['propertyname', 'city', 'name', 'email', 'phone'];
    const hasEmptyFields = requiredFields.some((field) => !formData[field]?.trim());
    const hasFormatErrors = Object.keys(errors).length > 0;
    return hasEmptyFields || hasFormatErrors;
  };

  const closeModal = () => setShowDeleteModal(false);

  return (
    <div className="mx-auto px-4 py-8">
      <ToastContainer />

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={closeModal}
        onConfirm={deleteConfig.onConfirm}
        title={deleteConfig.title}
        message={deleteConfig.message}
        loading={isDeleting[deleteConfig.type]}
      />

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Tabs */}
        <div className="flex border-b overflow-x-auto">
          {[
            ['basic', 'Basic Information'],
            ['business', 'Business Details'],
            ['banking', 'Banking Information'],
            ['regional', 'Regional Settings'],
            ['media', 'Media & Branding'],
            ['rules', 'Hotel Rules'],
          ].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setActiveTab(k)}
              className={`px-6 py-3 font-medium whitespace-nowrap ${
                activeTab === k
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-blue-500'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Form Content */}
        <div className="p-6">
          {message && (
            <div
              className={`mb-6 p-4 rounded-lg ${message.includes('✅') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
            >
              {message}
            </div>
          )}

          {property ? (
            <form onSubmit={handleUpdate}>
              {/* Basic Information */}
              {activeTab === 'basic' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[
                    { name: 'propertyname', label: 'Property Name *', type: 'text' },
                    { name: 'city', label: 'City *', type: 'text' },
                    { name: 'name', label: 'Hotel Name *', type: 'text' },
                    { name: 'location', label: 'Location', type: 'text' },
                    { name: 'address', label: 'Address', type: 'text' },
                    {
                      name: 'phone',
                      label: 'Mobile Number *',
                      type: 'tel',
                      placeholder: 'Enter your Mobile number',
                    },
                    { name: 'email', label: 'Email *', type: 'email' },
                  ].map((field) => (
                    <div key={field.name} className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">
                        {field.label}
                      </label>
                      <input
                        type={field.type}
                        name={field.name}
                        value={formData[field.name] || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder={field.placeholder}
                        className={`w-full px-4 py-2 border rounded-lg ${errors[field.name] ? 'border-red-500' : 'border-gray-300'}`}
                      />
                      {errors[field.name] && (
                        <p className="text-red-500 text-xs mt-1">{errors[field.name]}</p>
                      )}
                    </div>
                  ))}
                  <ReadOnly label="Property ID" value={property.propertyid} />
                  <ReadOnly label="Created By" value={property.created_by ?? '—'} />
                  <ReadOnly
                    label="Created Date"
                    value={
                      property.created_date ? new Date(property.created_date).toLocaleString() : '—'
                    }
                  />
                  <ReadOnly
                    label="Updated Date"
                    value={
                      property.updated_date ? new Date(property.updated_date).toLocaleString() : '—'
                    }
                  />
                  {/* With editable inputs */}
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">Check-in Time</label>
                    <input
                      type="time"
                      name="checkin_time"
                      value={formData.checkin_time || ''}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Check-out Time
                    </label>
                    <input
                      type="time"
                      name="checkout_time"
                      value={formData.checkout_time || ''}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
              )}

              {/* Business Details */}
              {activeTab === 'business' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[
                    ['gstnumber', 'GST Number'],
                    ['pan_number', 'PAN Number'],
                    ['fssai_number', 'FSSAI Number'],
                    ['cin_number', 'CIN Number'],
                    ['state_code', 'State Code'],
                  ].map(([name, label]) => (
                    <div key={name} className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">{label}</label>
                      <input
                        type="text"
                        name={name}
                        value={formData[name] || ''}
                        onChange={handleChange}
                        placeholder={`Enter ${label.toLowerCase()} (optional)`}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      />
                      <p className="text-xs text-gray-400">Optional</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Banking */}
              {activeTab === 'banking' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[
                    ['bank_account_name', 'Bank Account Name'],
                    ['bank_account_no', 'Bank Account Number'],
                    ['bank_ifsc', 'IFSC Code'],
                  ].map(([name, label]) => (
                    <div key={name} className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">{label}</label>
                      <input
                        type="text"
                        name={name}
                        value={formData[name] || ''}
                        onChange={handleChange}
                        placeholder={`Enter ${label.toLowerCase()} (optional)`}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      />
                      <p className="text-xs text-gray-400">Optional</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Regional Settings */}
              {activeTab === 'regional' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">Country</label>
                    <select
                      name="country"
                      value={formData.country || ''}
                      onChange={handleCountryChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">Select Country</option>
                      {COUNTRY_OPTIONS.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.name} ({c.code})
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-400">
                      Changing country auto-fills currency & timezone
                    </p>
                  </div>
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">Currency</label>
                    <select
                      name="currency"
                      value={formData.currency || ''}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      {CURRENCY_OPTIONS.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">Time Zone</label>
                    <select
                      name="time_zone"
                      value={formData.time_zone || ''}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      {TIMEZONE_OPTIONS.map((tz) => (
                        <option key={tz} value={tz}>
                          {tz}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Hotel Rules - property_hotel_policy CRUD */}
              {activeTab === 'rules' && (
                <div className="space-y-6">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        Hotel Rules & Policies
                      </h2>
                      <p className="mt-1 text-sm text-gray-500">
                        Configure check-in, guest, internet, parking, pet, smoking and hygiene
                        policies for this property.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {hotelPolicy && (
                        <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
                          Policy saved
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={loadHotelPolicy}
                        disabled={hotelPolicyLoading}
                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        {hotelPolicyLoading ? 'Refreshing...' : 'Refresh'}
                      </button>
                    </div>
                  </div>

                  {hotelPolicyLoading ? (
                    <div className="py-12 text-center text-sm text-gray-500">
                      Loading hotel rules...
                    </div>
                  ) : (
                    <>
                      <div className="space-y-7">
                        {HOTEL_POLICY_SECTIONS.map((section) => (
                          <section
                            key={section.title}
                            className="rounded-xl border border-gray-200 p-5"
                          >
                            <h3 className="mb-4 text-base font-semibold text-gray-800">
                              {section.title}
                            </h3>
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                              {section.fields.map((field) => (
                                <div
                                  key={field.name}
                                  className={`space-y-1 ${field.type === 'textarea' ? 'md:col-span-2' : ''}`}
                                >
                                  {field.type === 'checkbox' ? (
                                    <label className="flex min-h-10 items-center gap-3 text-sm font-medium text-gray-700">
                                      <input
                                        type="checkbox"
                                        name={field.name}
                                        checked={Boolean(hotelPolicyForm[field.name])}
                                        onChange={handleHotelPolicyChange}
                                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                      />
                                      {field.label}
                                    </label>
                                  ) : (
                                    <>
                                      <label className="block text-sm font-medium text-gray-700">
                                        {field.label}
                                        {field.required ? ' *' : ''}
                                      </label>
                                      {field.type === 'textarea' ? (
                                        <textarea
                                          name={field.name}
                                          value={hotelPolicyForm[field.name] ?? ''}
                                          onChange={handleHotelPolicyChange}
                                          rows={3}
                                          className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:outline-none"
                                        />
                                      ) : field.type === 'select' ? (
                                        <select
                                          name={field.name}
                                          value={hotelPolicyForm[field.name] ?? ''}
                                          onChange={handleHotelPolicyChange}
                                          className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:outline-none"
                                        >
                                          {field.options.map((option) => (
                                            <option key={option} value={option}>
                                              {option}
                                            </option>
                                          ))}
                                        </select>
                                      ) : (
                                        <input
                                          type={field.type}
                                          name={field.name}
                                          value={hotelPolicyForm[field.name] ?? ''}
                                          onChange={handleHotelPolicyChange}
                                          required={Boolean(field.required)}
                                          min={field.min}
                                          max={field.max}
                                          step={field.step}
                                          placeholder={
                                            field.type === 'url' ? 'https://example.com' : ''
                                          }
                                          className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:outline-none"
                                        />
                                      )}
                                    </>
                                  )}
                                </div>
                              ))}
                            </div>
                          </section>
                        ))}

                        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-5">
                          <div className="text-xs text-gray-500">
                            {hotelPolicy
                              ? `Policy ID: ${hotelPolicy.id} · Property ID: ${property.propertyid}`
                              : `No hotel policy exists yet · Property ID: ${property.propertyid}`}
                          </div>
                          <div className="flex gap-2">
                            {hotelPolicy && (
                              <button
                                type="button"
                                onClick={handleHotelPolicyDelete}
                                disabled={hotelPolicyDeleting || hotelPolicySaving}
                                className="rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                              >
                                {hotelPolicyDeleting ? 'Deleting...' : 'Delete Policy'}
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={handleHotelPolicySave}
                              disabled={hotelPolicySaving || hotelPolicyDeleting}
                              className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {hotelPolicySaving
                                ? 'Saving...'
                                : hotelPolicy
                                  ? 'Update Hotel Rules'
                                  : 'Create Hotel Rules'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ==================== MEDIA & BRANDING TAB ==================== */}
              {activeTab === 'media' && (
                <div className="space-y-10">
                  {/* --- Section 1: Logo / Homepage / Video --- */}
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <h1 className="font-semibold text-xl">Property Media Settings</h1>
                        <h1 className="font-semibold text-sm text-gray-400">
                          Manage your property's homepage visuals with ease—update featured videos,
                          images, and logos to keep your brand fresh and engaging.
                        </h1>
                      </div>
                      {(property.logo_url ||
                        property.homepage_url ||
                        property.homepage_video_url) && (
                        <button
                          type="button"
                          onClick={handleDeleteAllMedia}
                          className="px-4 py-2 bg-red-100 text-red-600 hover:bg-red-200 font-medium rounded-lg text-sm transition-colors"
                        >
                          Delete All Media
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Logo Upload */}
                      <div className="space-y-4">
                        <div className="space-y-1">
                          <label className="block text-sm font-medium text-gray-700">
                            Logo Image
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => setLogoFile(e.target.files?.[0])}
                              className="flex-1 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition"
                            />
                            {(logoFile || property.logo_url) && (
                              <button
                                type="button"
                                onClick={handleDeleteLogo}
                                disabled={isDeleting.logo}
                                className={`px-3 py-2 text-sm rounded transition whitespace-nowrap ${
                                  isDeleting.logo
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-red-500 text-white hover:bg-red-600'
                                }`}
                              >
                                {isDeleting.logo ? 'Deleting...' : 'Delete Logo'}
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="flex justify-center">
                          {logoFile ? (
                            <div className="relative">
                              <img
                                src={URL.createObjectURL(logoFile)}
                                alt="Preview Logo"
                                className="h-32 rounded-lg shadow-md object-contain"
                              />
                              <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded">
                                New
                              </div>
                            </div>
                          ) : property.logo_url ? (
                            <div className="relative">
                              <img
                                src={property.logo_url}
                                alt="Logo"
                                className="h-32 rounded-lg shadow-md object-contain"
                              />
                              <div className="absolute top-2 right-2 bg-gray-500 text-white text-xs px-2 py-1 rounded">
                                Current
                              </div>
                            </div>
                          ) : (
                            <div className="h-32 w-full border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                              <span className="text-gray-400">No logo uploaded</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Homepage Upload */}
                      <div className="space-y-4">
                        <div className="space-y-1">
                          <label className="block text-sm font-medium text-gray-700">
                            Homepage Image
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => setHomepageFile(e.target.files?.[0])}
                              className="flex-1 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition"
                            />
                            {(homepageFile || property.homepage_url) && (
                              <button
                                type="button"
                                onClick={handleDeleteHomepageImage}
                                disabled={isDeleting.homepageImage}
                                className={`px-3 py-2 text-sm rounded transition whitespace-nowrap ${
                                  isDeleting.homepageImage
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-red-500 text-white hover:bg-red-600'
                                }`}
                              >
                                {isDeleting.homepageImage ? 'Deleting...' : 'Delete Image'}
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="flex justify-center">
                          {homepageFile ? (
                            <div className="relative w-full">
                              <img
                                src={URL.createObjectURL(homepageFile)}
                                alt="Preview Homepage"
                                className="h-48 w-full rounded-lg shadow-md object-cover"
                              />
                              <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded">
                                New
                              </div>
                            </div>
                          ) : property.homepage_url ? (
                            <div className="relative w-full">
                              <img
                                src={property.homepage_url}
                                alt="Homepage"
                                className="h-48 w-full rounded-lg shadow-md object-cover"
                              />
                              <div className="absolute top-2 right-2 bg-gray-500 text-white text-xs px-2 py-1 rounded">
                                Current
                              </div>
                            </div>
                          ) : (
                            <div className="h-48 w-full border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                              <span className="text-gray-400">No homepage image uploaded</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Video Upload */}
                      <div className="space-y-4 md:col-span-2">
                        <div className="space-y-1">
                          <label className="block text-sm font-medium text-gray-700">
                            Property Video
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="file"
                              accept="video/*"
                              onChange={(e) => setVideoFile(e.target.files?.[0])}
                              className="flex-1 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition"
                            />
                            {(videoFile || property.homepage_video_url) && (
                              <button
                                type="button"
                                onClick={handleDeleteVideo}
                                disabled={isDeleting.video}
                                className={`px-3 py-2 text-sm rounded transition whitespace-nowrap ${
                                  isDeleting.video
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-red-500 text-white hover:bg-red-600'
                                }`}
                              >
                                {isDeleting.video ? 'Deleting...' : 'Delete Video'}
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="flex justify-center">
                          {videoFile ? (
                            <div className="relative w-full max-w-2xl">
                              <video controls className="w-full rounded-lg shadow-md">
                                <source src={URL.createObjectURL(videoFile)} type="video/mp4" />
                              </video>
                              <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded">
                                New
                              </div>
                            </div>
                          ) : property.homepage_video_url ? (
                            <div className="relative w-full max-w-xl">
                              <video controls className="w-full rounded-lg shadow-md">
                                <source src={property.homepage_video_url} type="video/mp4" />
                              </video>
                              <div className="absolute top-2 right-2 bg-gray-500 text-white text-xs px-2 py-1 rounded">
                                Current
                              </div>
                            </div>
                          ) : (
                            <div className="w-full max-w-xl border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center py-16">
                              <span className="text-gray-400">No video uploaded</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* --- Section 2: Gallery (INLINE, no separate component) --- */}
                  <div className="border-t pt-8">
                    <h3 className="font-semibold text-lg mb-4">Gallery Images & Videos</h3>

                    {/* Upload form */}
                    <div className="bg-gray-50 rounded-xl p-4 mb-6">
                      <h4 className="font-medium mb-3">Upload New Gallery Item</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Media Type
                          </label>
                          <select
                            value={galleryMediaType}
                            onChange={(e) => setGalleryMediaType(e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg"
                          >
                            <option value="image">Image</option>
                            <option value="video">Video</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Category
                          </label>
                          <select
                            value={galleryCategory}
                            onChange={(e) => setGalleryCategory(e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg"
                          >
                            {(galleryCategories.length ? galleryCategories : ['general']).map(
                              (c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              )
                            )}
                          </select>
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700">File *</label>
                          <input
                            type="file"
                            accept={galleryMediaType === 'image' ? 'image/*' : 'video/*'}
                            onChange={(e) => setGalleryFile(e.target.files?.[0] || null)}
                            className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Title</label>
                          <input
                            value={galleryTitle}
                            onChange={(e) => setGalleryTitle(e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Display Order
                          </label>
                          <input
                            type="number"
                            value={galleryOrder}
                            onChange={(e) => setGalleryOrder(parseInt(e.target.value, 10) || 0)}
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Description
                          </label>
                          <textarea
                            value={galleryDescription}
                            onChange={(e) => setGalleryDescription(e.target.value)}
                            rows={2}
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <button
                            type="button"
                            onClick={handleGalleryUpload}
                            disabled={galleryUploading}
                            className="px-5 py-2 bg-black text-white rounded-lg disabled:opacity-60"
                          >
                            {galleryUploading ? 'Uploading...' : 'Upload Gallery Item'}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Filters + List */}
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                      <h4 className="font-medium">Gallery ({galleryItems.length})</h4>
                      <div className="flex flex-wrap gap-2">
                        <select
                          value={galleryFilterType}
                          onChange={(e) => setGalleryFilterType(e.target.value)}
                          className="px-3 py-2 border rounded-lg"
                        >
                          <option value="">All Types</option>
                          <option value="image">Images</option>
                          <option value="video">Videos</option>
                        </select>
                        <select
                          value={galleryFilterCategory}
                          onChange={(e) => setGalleryFilterCategory(e.target.value)}
                          className="px-3 py-2 border rounded-lg"
                        >
                          <option value="">All Categories</option>
                          {galleryCategories.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                        {(galleryFilterType || galleryFilterCategory) && (
                          <button
                            type="button"
                            onClick={handleGalleryBulkDelete}
                            className="px-3 py-2 bg-red-100 text-red-700 rounded-lg"
                          >
                            Delete Filtered
                          </button>
                        )}
                      </div>
                    </div>

                    {galleryLoading ? (
                      <p>Loading gallery...</p>
                    ) : galleryItems.length === 0 ? (
                      <p className="text-gray-500">No gallery media yet.</p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {galleryItems.map((item) => (
                          <div key={item.id} className="border rounded-lg overflow-hidden">
                            {item.media_type === 'image' ? (
                              <img
                                src={item.media_url}
                                alt={item.title || ''}
                                className="w-full h-40 object-cover"
                              />
                            ) : (
                              <video controls className="w-full h-40 object-cover">
                                <source src={item.media_url} />
                              </video>
                            )}
                            <div className="p-3 space-y-1">
                              <div className="flex justify-between text-xs text-gray-500">
                                <span className="capitalize">{item.category}</span>
                                <span
                                  className={item.is_active ? 'text-green-600' : 'text-red-500'}
                                >
                                  {item.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                              {item.title && (
                                <p className="font-medium text-sm truncate">{item.title}</p>
                              )}
                              <div className="flex gap-2 pt-2">
                                <button
                                  type="button"
                                  onClick={() => setEditingItem({ ...item })}
                                  className="flex-1 text-xs px-2 py-1 bg-gray-100 rounded"
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleGalleryToggleActive(item)}
                                  className="flex-1 text-xs px-2 py-1 bg-gray-100 rounded"
                                >
                                  {item.is_active ? 'Disable' : 'Enable'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleGalleryDelete(item)}
                                  className="flex-1 text-xs px-2 py-1 bg-red-100 text-red-700 rounded"
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Inline Edit Modal */}
                  {editingItem && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                      <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
                        <h3 className="text-lg font-semibold mb-4">Edit Gallery Item</h3>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Category
                            </label>
                            <select
                              value={editingItem.category}
                              onChange={(e) =>
                                setEditingItem({ ...editingItem, category: e.target.value })
                              }
                              className="w-full px-3 py-2 border rounded-lg"
                            >
                              {galleryCategories.map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Title</label>
                            <input
                              value={editingItem.title || ''}
                              onChange={(e) =>
                                setEditingItem({ ...editingItem, title: e.target.value })
                              }
                              className="w-full px-3 py-2 border rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Description
                            </label>
                            <textarea
                              value={editingItem.description || ''}
                              onChange={(e) =>
                                setEditingItem({ ...editingItem, description: e.target.value })
                              }
                              rows={2}
                              className="w-full px-3 py-2 border rounded-lg"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700">
                                Display Order
                              </label>
                              <input
                                type="number"
                                value={editingItem.display_order ?? 0}
                                onChange={(e) =>
                                  setEditingItem({
                                    ...editingItem,
                                    display_order: parseInt(e.target.value, 10) || 0,
                                  })
                                }
                                className="w-full px-3 py-2 border rounded-lg"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">
                                Active
                              </label>
                              <select
                                value={String(editingItem.is_active)}
                                onChange={(e) =>
                                  setEditingItem({
                                    ...editingItem,
                                    is_active: e.target.value === 'true',
                                  })
                                }
                                className="w-full px-3 py-2 border rounded-lg"
                              >
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                              </select>
                            </div>
                          </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-5">
                          <button
                            type="button"
                            onClick={() => setEditingItem(null)}
                            className="px-4 py-2 bg-gray-100 rounded-lg"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={handleGalleryUpdate}
                            className="px-4 py-2 bg-black text-white rounded-lg"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Save property details (Hotel Rules has its own CRUD submit) */}
              {activeTab !== 'rules' && (
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <button
                    type="submit"
                    disabled={isSubmitting || hasValidationErrors()}
                    className={`w-full md:w-auto px-6 py-3 bg-black text-white font-semibold rounded-lg shadow-md hover:text-gray-200 focus:outline-none ${
                      isSubmitting || hasValidationErrors() ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Saving...
                      </span>
                    ) : (
                      ' Save Changes'
                    )}
                  </button>
                </div>
              )}
            </form>
          ) : (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PropertySettings;

// import React, { useEffect, useState } from 'react';
// import { getCurrentUser } from '../../Authentication/auth';
// import { ToastContainer, toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// // Custom Modal Component
// const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, loading }) => {
//   if (!isOpen) return null;

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//       <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
//         <div className="mb-4">
//           <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
//           <p className="mt-2 text-sm text-gray-600">{message}</p>
//         </div>

//         <div className="flex justify-end space-x-3">
//           <button
//             onClick={onClose}
//             disabled={loading}
//             className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
//           >
//             Cancel
//           </button>
//           <button
//             onClick={onConfirm}
//             disabled={loading}
//             className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
//           >
//             {loading ? (
//               <span className="flex items-center">
//                 <svg
//                   className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
//                   fill="none"
//                   viewBox="0 0 24 24"
//                 >
//                   <circle
//                     className="opacity-25"
//                     cx="12"
//                     cy="12"
//                     r="10"
//                     stroke="currentColor"
//                     strokeWidth="4"
//                   ></circle>
//                   <path
//                     className="opacity-75"
//                     fill="currentColor"
//                     d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
//                   ></path>
//                 </svg>
//                 Deleting...
//               </span>
//             ) : (
//               'Delete'
//             )}
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// const PropertySettings = () => {
//   const [property, setProperty] = useState(null);
//   const [formData, setFormData] = useState({
//     propertyname: '',
//     city: '',
//     name: '',
//     location: '',
//     address: '',
//     phone: '',
//     email: '',
//     maxGuestsPerRoom: 2,
//     checkInTime: '14:00',
//     checkOutTime: '12:00',
//     cancellationPolicy: 'Free cancellation up to 24 hours before check-in',
//     petPolicy: 'Not allowed',
//     smokingPolicy: 'Not allowed',
//     extraBedPolicy: 'Available with additional charge',
//     hasWifi: true,
//     hasPool: false,
//     hasGym: false,
//     hasRestaurant: true,
//     hasParking: true,
//     hasSpa: false,
//     // New fields from API
//     gstnumber: '',
//     pan_number: '',
//     fssai_number: '',
//     cin_number: '',
//     state_code: '',
//     bank_account_name: '',
//     bank_account_no: '',
//     bank_ifsc: '',
//   });

//   const [logoFile, setLogoFile] = useState(null);
//   const [videoFile, setVideoFile] = useState(null);
//   const [homepageFile, setHomepageFile] = useState(null);
//   const [message, setMessage] = useState('');
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [activeTab, setActiveTab] = useState('basic');
//   const [errors, setErrors] = useState({});
//   const [isDeleting, setIsDeleting] = useState({
//     logo: false,
//     homepageImage: false,
//     video: false,
//     all: false,
//   });

//   // Modal states
//   const [showDeleteModal, setShowDeleteModal] = useState(false);
//   const [deleteConfig, setDeleteConfig] = useState({
//     type: '',
//     title: '',
//     message: '',
//     onConfirm: () => {},
//   });

//   useEffect(() => {
//     fetchProperty();
//   }, []);

//   const validateEmail = (email) => {
//     const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
//     return emailRegex.test(email);
//   };

//   const validateIndianPhone = (phone) => {
//     const phoneRegex = /^\d{10}$/;
//     return phoneRegex.test(phone);
//   };

//   const validateField = (name, value) => {
//     const newErrors = { ...errors };

//     switch (name) {
//       case 'email':
//         if (value && !validateEmail(value)) {
//           newErrors.email = 'Please enter a valid email address';
//         } else {
//           delete newErrors.email;
//         }
//         break;

//       case 'phone':
//         if (value) {
//           if (!validateIndianPhone(value)) {
//             newErrors.phone = 'Please enter a valid 10-digit mobile number';
//           } else {
//             delete newErrors.phone;
//           }
//         } else {
//           delete newErrors.phone;
//         }
//         break;

//       case 'propertyname':
//         if (!value?.trim()) {
//           newErrors.propertyname = 'Property name is required';
//         } else {
//           delete newErrors.propertyname;
//         }
//         break;

//       case 'city':
//         if (!value?.trim()) {
//           newErrors.city = 'City is required';
//         } else {
//           delete newErrors.city;
//         }
//         break;

//       case 'name':
//         if (!value?.trim()) {
//           newErrors.name = 'Hotel name is required';
//         } else {
//           delete newErrors.name;
//         }
//         break;

//       default:
//         break;
//     }

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   const fetchProperty = async () => {
//     try {
//       const currentUser = await getCurrentUser();
//       if (!currentUser) {
//         toast.error('Failed to fetch user data. Please log in again.');
//         return;
//       }
//       const res = await fetch(`${BQ_BASE_URL}/bq/api/properties/${currentUser.property_id}`);
//       const data = await res.json();
//       setProperty(data);

//       // Format the time fields properly
//       const formattedData = {
//         ...data,
//         checkInTime: data.checkin_time
//           ? data.checkin_time.split('T')[1]?.substring(0, 5) || '14:00'
//           : '14:00',
//         checkOutTime: data.checkout_time
//           ? data.checkout_time.split('T')[1]?.substring(0, 5) || '12:00'
//           : '12:00',
//       };

//       setFormData((prev) => ({ ...prev, ...formattedData }));
//     } catch (err) {
//       console.error('Failed to fetch property:', err);
//       toast.error('Failed to fetch property details');
//     }
//   };

//   const handleChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     const newValue = type === 'checkbox' ? checked : value;

//     setFormData((prev) => ({
//       ...prev,
//       [name]: newValue,
//     }));

//     // Only validate email and phone, all other fields are optional
//     if (name === 'email' || name === 'phone') {
//       validateField(name, newValue);
//     }
//   };

//   const handleBlur = (e) => {
//     const { name, value } = e.target;
//     // Only validate email and phone, all other fields are optional
//     if (name === 'email' || name === 'phone') {
//       validateField(name, value);
//     }
//   };

//   const handleUpdate = async (e) => {
//     e.preventDefault();
//     setIsSubmitting(true);
//     setMessage('');

//     const requiredFields = ['propertyname', 'city', 'name', 'email', 'phone'];
//     let hasErrors = false;
//     const newErrors = {};

//     requiredFields.forEach((field) => {
//       if (!formData[field]?.trim()) {
//         newErrors[field] = `${
//           field === 'propertyname'
//             ? 'Property name'
//             : field === 'city'
//               ? 'City'
//               : field === 'name'
//                 ? 'Hotel name'
//                 : field
//         } is required`;
//         hasErrors = true;
//       }
//     });

//     if (formData.email && !validateEmail(formData.email)) {
//       newErrors.email = 'Please enter a valid email address';
//       hasErrors = true;
//     }

//     if (formData.phone && !validateIndianPhone(formData.phone)) {
//       newErrors.phone = 'Please enter a valid 10-digit mobile number';
//       hasErrors = true;
//     }

//     if (hasErrors) {
//       setErrors(newErrors);
//       setIsSubmitting(false);
//       toast.error('Please fix the validation errors before submitting.');
//       return;
//     }

//     const form = new FormData();

//     // List of all fields that should be sent to the API
//     const allFields = [
//       'propertyname',
//       'city',
//       'name',
//       'location',
//       'address',
//       'phone',
//       'email',
//       'maxGuestsPerRoom',
//       'checkInTime',
//       'checkOutTime',
//       'cancellationPolicy',
//       'petPolicy',
//       'smokingPolicy',
//       'extraBedPolicy',
//       'hasWifi',
//       'hasPool',
//       'hasGym',
//       'hasRestaurant',
//       'hasParking',
//       'hasSpa',
//       // New fields
//       'gstnumber',
//       'pan_number',
//       'fssai_number',
//       'cin_number',
//       'state_code',
//       'bank_account_name',
//       'bank_account_no',
//       'bank_ifsc',
//     ];

//     // Send all fields, using empty string for blank optional fields
//     allFields.forEach((key) => {
//       let value = formData[key];

//       // Convert boolean values to strings
//       if (typeof value === 'boolean') {
//         value = value.toString();
//       }

//       // For optional fields, send empty string if blank
//       if (value === null || value === undefined) {
//         value = '';
//       }

//       form.append(key, value.toString());
//     });

//     // Add files if they exist
//     if (logoFile) form.append('logo_file', logoFile);
//     if (homepageFile) form.append('homepage_file', homepageFile);
//     if (videoFile) form.append('video_file', videoFile);

//     try {
//       const currentUser = await getCurrentUser();
//       if (!currentUser) {
//         toast.error('Failed to fetch user data. Please log in again.');
//         setIsSubmitting(false);
//         return;
//       }

//       const res = await fetch(`${BQ_BASE_URL}/bq/api/properties/${currentUser.property_id}`, {
//         method: 'PUT',
//         body: form,
//       });

//       if (!res.ok) {
//         const errorData = await res.text();
//         console.error('API Error:', errorData);
//         throw new Error('Failed to update property');
//       }

//       const updated = await res.json();

//       setProperty(updated);
//       setFormData((prev) => ({ ...prev, ...updated }));
//       setLogoFile(null);
//       setHomepageFile(null);
//       setVideoFile(null);
//       setMessage('✅ Property updated successfully!');
//       toast.success('Property updated successfully!');
//     } catch (err) {
//       console.error(err);
//       setMessage('❌ Failed to update property');
//       toast.error('Failed to update property');
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   // Show delete confirmation modal
//   const showDeleteConfirmation = (type, title, message, onConfirmCallback) => {
//     setDeleteConfig({
//       type,
//       title,
//       message,
//       onConfirm: () => {
//         setShowDeleteModal(false);
//         onConfirmCallback();
//       },
//     });
//     setShowDeleteModal(true);
//   };

//   // API call to delete media
//   const deleteMedia = async (endpoint, mediaType) => {
//     try {
//       const currentUser = await getCurrentUser();
//       if (!currentUser || !currentUser.property_id) {
//         toast.error('Failed to fetch user data. Please log in again.');
//         return;
//       }

//       setIsDeleting((prev) => ({ ...prev, [mediaType]: true }));

//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/properties/${currentUser.property_id}${endpoint}`,
//         {
//           method: 'DELETE',
//           headers: {
//             'Content-Type': 'application/json',
//           },
//         }
//       );

//       if (!response.ok) {
//         throw new Error(`Failed to delete ${mediaType}`);
//       }

//       const result = await response.json();

//       // Update local state
//       setProperty((prev) => ({
//         ...prev,
//         logo_url: mediaType === 'logo' ? null : prev.logo_url,
//         homepage_url: mediaType === 'homepage' ? null : prev.homepage_url,
//         homepage_video_url: mediaType === 'homepage_video' ? null : prev.homepage_video_url,
//       }));

//       setFormData((prev) => ({
//         ...prev,
//         logo_url: mediaType === 'logo' ? null : prev.logo_url,
//         homepage_url: mediaType === 'homepage' ? null : prev.homepage_url,
//         homepage_video_url: mediaType === 'homepage_video' ? null : prev.homepage_video_url,
//       }));

//       toast.success(
//         mediaType === 'logo'
//           ? 'Logo deleted successfully!'
//           : mediaType === 'homepage'
//             ? 'Homepage image deleted successfully!'
//             : 'Video deleted successfully!'
//       );

//       // Clear file state if it was selected but not uploaded
//       if (mediaType === 'logo') setLogoFile(null);
//       if (mediaType === 'homepage') setHomepageFile(null);
//       if (mediaType === 'homepage_video') setVideoFile(null);

//       return result;
//     } catch (error) {
//       console.error(`Error deleting ${mediaType}:`, error);
//       toast.error(`Failed to delete ${mediaType}: ${error.message}`);
//       return null;
//     } finally {
//       setIsDeleting((prev) => ({ ...prev, [mediaType]: false }));
//     }
//   };

//   const handleDeleteLogo = () => {
//     showDeleteConfirmation(
//       'logo',
//       'Delete Logo',
//       'Are you sure you want to delete the logo? This action cannot be undone.',
//       async () => {
//         const toastId = toast.loading('Deleting logo...');
//         try {
//           const result = await deleteMedia('/logo', 'logo');
//           if (result) {
//             toast.update(toastId, {
//               render: 'Logo deleted successfully!',
//               type: 'success',
//               isLoading: false,
//               autoClose: 3000,
//             });
//           } else {
//             toast.update(toastId, {
//               render: 'Failed to delete logo',
//               type: 'error',
//               isLoading: false,
//               autoClose: 3000,
//             });
//           }
//         } catch (error) {
//           toast.update(toastId, {
//             render: 'Error deleting logo',
//             type: 'error',
//             isLoading: false,
//             autoClose: 3000,
//           });
//         }
//       }
//     );
//   };

//   const handleDeleteHomepageImage = () => {
//     showDeleteConfirmation(
//       'homepageImage',
//       'Delete Homepage Image',
//       'Are you sure you want to delete the homepage image? This action cannot be undone.',
//       async () => {
//         const toastId = toast.loading('Deleting homepage image...');
//         try {
//           const result = await deleteMedia('/homepage-image', 'homepage');
//           if (result) {
//             toast.update(toastId, {
//               render: 'Homepage image deleted successfully!',
//               type: 'success',
//               isLoading: false,
//               autoClose: 3000,
//             });
//           } else {
//             toast.update(toastId, {
//               render: 'Failed to delete homepage image',
//               type: 'error',
//               isLoading: false,
//               autoClose: 3000,
//             });
//           }
//         } catch (error) {
//           toast.update(toastId, {
//             render: 'Error deleting homepage image',
//             type: 'error',
//             isLoading: false,
//             autoClose: 3000,
//           });
//         }
//       }
//     );
//   };

//   const handleDeleteVideo = () => {
//     showDeleteConfirmation(
//       'video',
//       'Delete Property Video',
//       'Are you sure you want to delete the property video? This action cannot be undone.',
//       async () => {
//         const toastId = toast.loading('Deleting video...');
//         try {
//           const result = await deleteMedia('/homepage-video', 'homepage_video');
//           if (result) {
//             toast.update(toastId, {
//               render: 'Video deleted successfully!',
//               type: 'success',
//               isLoading: false,
//               autoClose: 3000,
//             });
//           } else {
//             toast.update(toastId, {
//               render: 'Failed to delete video',
//               type: 'error',
//               isLoading: false,
//               autoClose: 3000,
//             });
//           }
//         } catch (error) {
//           toast.update(toastId, {
//             render: 'Error deleting video',
//             type: 'error',
//             isLoading: false,
//             autoClose: 3000,
//           });
//         }
//       }
//     );
//   };

//   const handleDeleteAllMedia = () => {
//     showDeleteConfirmation(
//       'all',
//       'Delete All Media',
//       'Are you sure you want to delete ALL media (logo, homepage image, and video)? This action cannot be undone.',
//       async () => {
//         const toastId = toast.loading('Deleting all media...');
//         try {
//           const currentUser = await getCurrentUser();
//           if (!currentUser || !currentUser.property_id) {
//             toast.error('Failed to fetch user data. Please log in again.');
//             return;
//           }

//           setIsDeleting((prev) => ({ ...prev, all: true }));

//           const response = await fetch(
//             `${BQ_BASE_URL}/bq/api/properties/${currentUser.property_id}/all-media`,
//             {
//               method: 'DELETE',
//               headers: {
//                 'Content-Type': 'application/json',
//               },
//             }
//           );

//           if (!response.ok) {
//             throw new Error('Failed to delete all media');
//           }

//           const result = await response.json();

//           setProperty((prev) => ({
//             ...prev,
//             logo_url: null,
//             homepage_url: null,
//             homepage_video_url: null,
//           }));

//           setFormData((prev) => ({
//             ...prev,
//             logo_url: null,
//             homepage_url: null,
//             homepage_video_url: null,
//           }));

//           setLogoFile(null);
//           setHomepageFile(null);
//           setVideoFile(null);

//           toast.update(toastId, {
//             render: 'All media deleted successfully!',
//             type: 'success',
//             isLoading: false,
//             autoClose: 3000,
//           });

//           return result;
//         } catch (error) {
//           console.error('Error deleting all media:', error);
//           toast.update(toastId, {
//             render: 'Failed to delete all media',
//             type: 'error',
//             isLoading: false,
//             autoClose: 3000,
//           });
//           return null;
//         } finally {
//           setIsDeleting((prev) => ({ ...prev, all: false }));
//         }
//       }
//     );
//   };

//   const hasValidationErrors = () => {
//     const requiredFields = ['propertyname', 'city', 'name', 'email', 'phone'];
//     const hasEmptyFields = requiredFields.some((field) => !formData[field]?.trim());
//     const hasFormatErrors = Object.keys(errors).length > 0;

//     return hasEmptyFields || hasFormatErrors;
//   };

//   const closeModal = () => {
//     setShowDeleteModal(false);
//   };

//   return (
//     <div className="mx-auto px-4 py-8">
//       <ToastContainer />

//       {/* Delete Confirmation Modal */}
//       <DeleteConfirmationModal
//         isOpen={showDeleteModal}
//         onClose={closeModal}
//         onConfirm={deleteConfig.onConfirm}
//         title={deleteConfig.title}
//         message={deleteConfig.message}
//         loading={isDeleting[deleteConfig.type]}
//       />

//       <div className="bg-white rounded-xl shadow-lg overflow-hidden">
//         {/* Tabs */}
//         <div className="flex border-b overflow-x-auto">
//           <button
//             onClick={() => setActiveTab('basic')}
//             className={`px-6 py-3 font-medium whitespace-nowrap ${
//               activeTab === 'basic'
//                 ? 'text-blue-600 border-b-2 border-blue-600'
//                 : 'text-gray-600 hover:text-blue-500'
//             }`}
//           >
//             Basic Information
//           </button>
//           <button
//             onClick={() => setActiveTab('business')}
//             className={`px-6 py-3 font-medium whitespace-nowrap ${
//               activeTab === 'business'
//                 ? 'text-blue-600 border-b-2 border-blue-600'
//                 : 'text-gray-600 hover:text-blue-500'
//             }`}
//           >
//             Business Details
//           </button>
//           <button
//             onClick={() => setActiveTab('banking')}
//             className={`px-6 py-3 font-medium whitespace-nowrap ${
//               activeTab === 'banking'
//                 ? 'text-blue-600 border-b-2 border-blue-600'
//                 : 'text-gray-600 hover:text-blue-500'
//             }`}
//           >
//             Banking Information
//           </button>
//           <button
//             onClick={() => setActiveTab('media')}
//             className={`px-6 py-3 font-medium whitespace-nowrap ${
//               activeTab === 'media'
//                 ? 'text-blue-600 border-b-2 border-blue-600'
//                 : 'text-gray-600 hover:text-blue-500'
//             }`}
//           >
//             Media & Branding
//           </button>
//           <button
//             onClick={() => setActiveTab('rules')}
//             className={`px-6 py-3 font-medium whitespace-nowrap ${
//               activeTab === 'rules'
//                 ? 'text-blue-600 border-b-2 border-blue-600'
//                 : 'text-gray-600 hover:text-blue-500'
//             }`}
//           >
//             Hotel Rules
//           </button>
//         </div>

//         {/* Form Content */}
//         <div className="p-6">
//           {message && (
//             <div
//               className={`mb-6 p-4 rounded-lg ${
//                 message.includes('✅') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
//               }`}
//             >
//               {message}
//             </div>
//           )}

//           {property ? (
//             <form onSubmit={handleUpdate}>
//               {/* Basic Information Tab */}
//               {activeTab === 'basic' && (
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                   {[
//                     {
//                       name: 'propertyname',
//                       label: 'Property Name *',
//                       type: 'text',
//                       required: true,
//                     },
//                     {
//                       name: 'city',
//                       label: 'City *',
//                       type: 'text',
//                       required: true,
//                     },
//                     {
//                       name: 'name',
//                       label: 'Hotel Name *',
//                       type: 'text',
//                       required: true,
//                     },
//                     { name: 'location', label: 'Location', type: 'text' },
//                     { name: 'address', label: 'Address', type: 'text' },
//                     {
//                       name: 'phone',
//                       label: 'Mobile Number *',
//                       type: 'tel',
//                       required: true,
//                       placeholder: 'Enter your Mobile number',
//                     },
//                     {
//                       name: 'email',
//                       label: 'Email *',
//                       type: 'email',
//                       required: true,
//                     },
//                   ].map((field) => (
//                     <div key={field.name} className="space-y-1">
//                       <label className="block text-sm font-medium text-gray-700">
//                         {field.label}
//                       </label>
//                       <input
//                         type={field.type}
//                         name={field.name}
//                         value={formData[field.name] || ''}
//                         onChange={handleChange}
//                         onBlur={handleBlur}
//                         placeholder={field.placeholder}
//                         className={`w-full px-4 py-2 border rounded-lg ${
//                           errors[field.name] ? 'border-red-500' : 'border-gray-300'
//                         }`}
//                       />
//                       {errors[field.name] && (
//                         <p className="text-red-500 text-xs mt-1">{errors[field.name]}</p>
//                       )}
//                     </div>
//                   ))}
//                 </div>
//               )}

//               {/* Business Details Tab */}
//               {activeTab === 'business' && (
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                   <div className="space-y-1">
//                     <label className="block text-sm font-medium text-gray-700">GST Number</label>
//                     <input
//                       type="text"
//                       name="gstnumber"
//                       value={formData.gstnumber || ''}
//                       onChange={handleChange}
//                       placeholder="Enter GST number (optional)"
//                       className="w-full px-4 py-2 border border-gray-300 rounded-lg"
//                     />
//                     <p className="text-xs text-gray-400">Optional</p>
//                   </div>

//                   <div className="space-y-1">
//                     <label className="block text-sm font-medium text-gray-700">PAN Number</label>
//                     <input
//                       type="text"
//                       name="pan_number"
//                       value={formData.pan_number || ''}
//                       onChange={handleChange}
//                       placeholder="Enter PAN number (optional)"
//                       className="w-full px-4 py-2 border border-gray-300 rounded-lg"
//                     />
//                     <p className="text-xs text-gray-400">Optional</p>
//                   </div>

//                   <div className="space-y-1">
//                     <label className="block text-sm font-medium text-gray-700">FSSAI Number</label>
//                     <input
//                       type="text"
//                       name="fssai_number"
//                       value={formData.fssai_number || ''}
//                       onChange={handleChange}
//                       placeholder="Enter FSSAI number (optional)"
//                       className="w-full px-4 py-2 border border-gray-300 rounded-lg"
//                     />
//                     <p className="text-xs text-gray-400">Optional</p>
//                   </div>

//                   <div className="space-y-1">
//                     <label className="block text-sm font-medium text-gray-700">CIN Number</label>
//                     <input
//                       type="text"
//                       name="cin_number"
//                       value={formData.cin_number || ''}
//                       onChange={handleChange}
//                       placeholder="Enter CIN number (optional)"
//                       className="w-full px-4 py-2 border border-gray-300 rounded-lg"
//                     />
//                     <p className="text-xs text-gray-400">Optional</p>
//                   </div>

//                   <div className="space-y-1">
//                     <label className="block text-sm font-medium text-gray-700">State Code</label>
//                     <input
//                       type="text"
//                       name="state_code"
//                       value={formData.state_code || ''}
//                       onChange={handleChange}
//                       placeholder="Enter state code (optional)"
//                       className="w-full px-4 py-2 border border-gray-300 rounded-lg"
//                     />
//                     <p className="text-xs text-gray-400">Optional</p>
//                   </div>
//                 </div>
//               )}

//               {/* Banking Information Tab */}
//               {activeTab === 'banking' && (
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                   <div className="space-y-1">
//                     <label className="block text-sm font-medium text-gray-700">
//                       Bank Account Name
//                     </label>
//                     <input
//                       type="text"
//                       name="bank_account_name"
//                       value={formData.bank_account_name || ''}
//                       onChange={handleChange}
//                       placeholder="Enter account holder name (optional)"
//                       className="w-full px-4 py-2 border border-gray-300 rounded-lg"
//                     />
//                     <p className="text-xs text-gray-400">Optional</p>
//                   </div>

//                   <div className="space-y-1">
//                     <label className="block text-sm font-medium text-gray-700">
//                       Bank Account Number
//                     </label>
//                     <input
//                       type="text"
//                       name="bank_account_no"
//                       value={formData.bank_account_no || ''}
//                       onChange={handleChange}
//                       placeholder="Enter account number (optional)"
//                       className="w-full px-4 py-2 border border-gray-300 rounded-lg"
//                     />
//                     <p className="text-xs text-gray-400">Optional</p>
//                   </div>

//                   <div className="space-y-1">
//                     <label className="block text-sm font-medium text-gray-700">IFSC Code</label>
//                     <input
//                       type="text"
//                       name="bank_ifsc"
//                       value={formData.bank_ifsc || ''}
//                       onChange={handleChange}
//                       placeholder="Enter IFSC code (optional)"
//                       className="w-full px-4 py-2 border border-gray-300 rounded-lg"
//                     />
//                     <p className="text-xs text-gray-400">Optional</p>
//                   </div>
//                 </div>
//               )}

//               {/* Hotel Rules Tab */}
//               {activeTab === 'rules' && (
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                   <div className="space-y-1">
//                     <label className="block text-sm font-medium text-gray-700">
//                       Max Guests Per Room
//                     </label>
//                     <select
//                       name="maxGuestsPerRoom"
//                       value={formData.maxGuestsPerRoom}
//                       onChange={handleChange}
//                       className="w-full px-4 py-2 border border-gray-300 rounded-lg"
//                     >
//                       {[1, 2, 3, 4, 5].map((num) => (
//                         <option key={num} value={num}>
//                           {num} {num === 1 ? 'guest' : 'guests'}
//                         </option>
//                       ))}
//                     </select>
//                   </div>

//                   <div className="space-y-1">
//                     <label className="block text-sm font-medium text-gray-700">Check-in Time</label>
//                     <input
//                       type="time"
//                       name="checkInTime"
//                       value={formData.checkInTime}
//                       onChange={handleChange}
//                       className="w-full px-4 py-2 border border-gray-300 rounded-lg"
//                     />
//                   </div>

//                   <div className="space-y-1">
//                     <label className="block text-sm font-medium text-gray-700">
//                       Check-out Time
//                     </label>
//                     <input
//                       type="time"
//                       name="checkOutTime"
//                       value={formData.checkOutTime}
//                       onChange={handleChange}
//                       className="w-full px-4 py-2 border border-gray-300 rounded-lg"
//                     />
//                   </div>

//                   <div className="space-y-1">
//                     <label className="block text-sm font-medium text-gray-700">
//                       Cancellation Policy
//                     </label>
//                     <select
//                       name="cancellationPolicy"
//                       value={formData.cancellationPolicy}
//                       onChange={handleChange}
//                       className="w-full px-4 py-2 border border-gray-300 rounded-lg"
//                     >
//                       <option>Free cancellation up to 24 hours before check-in</option>
//                       <option>Free cancellation up to 48 hours before check-in</option>
//                       <option>Non-refundable</option>
//                       <option>Custom policy</option>
//                     </select>
//                   </div>

//                   <div className="space-y-1">
//                     <label className="block text-sm font-medium text-gray-700">Pet Policy</label>
//                     <select
//                       name="petPolicy"
//                       value={formData.petPolicy}
//                       onChange={handleChange}
//                       className="w-full px-4 py-2 border border-gray-300 rounded-lg"
//                     >
//                       <option>Not allowed</option>
//                       <option>Allowed with restrictions</option>
//                       <option>Allowed with additional charge</option>
//                       <option>Allowed (no charge)</option>
//                     </select>
//                   </div>

//                   <div className="space-y-1">
//                     <label className="block text-sm font-medium text-gray-700">
//                       Smoking Policy
//                     </label>
//                     <select
//                       name="smokingPolicy"
//                       value={formData.smokingPolicy}
//                       onChange={handleChange}
//                       className="w-full px-4 py-2 border border-gray-300 rounded-lg"
//                     >
//                       <option>Not allowed</option>
//                       <option>Allowed in designated areas</option>
//                       <option>Allowed in all areas</option>
//                     </select>
//                   </div>

//                   <div className="space-y-1">
//                     <label className="block text-sm font-medium text-gray-700">
//                       Extra Bed Policy
//                     </label>
//                     <select
//                       name="extraBedPolicy"
//                       value={formData.extraBedPolicy}
//                       onChange={handleChange}
//                       className="w-full px-4 py-2 border border-gray-300 rounded-lg"
//                     >
//                       <option>Not available</option>
//                       <option>Available with additional charge</option>
//                       <option>Available (no charge)</option>
//                     </select>
//                   </div>
//                 </div>
//               )}

//               {/* Media & Branding Tab */}
//               {activeTab === 'media' && (
//                 <div>
//                   <div className="flex justify-between items-center mb-4">
//                     <div>
//                       <h1 className="font-semibold text-xl">Property Media Settings</h1>
//                       <h1 className="font-semibold text-sm text-gray-400">
//                         Manage your property's homepage visuals with ease—update featured videos,
//                         images, and logos to keep your brand fresh and engaging.
//                       </h1>
//                     </div>
//                     {(property.logo_url ||
//                       property.homepage_url ||
//                       property.homepage_video_url) && (
//                       <button
//                         type="button"
//                         onClick={handleDeleteAllMedia}
//                         className="px-4 py-2 bg-red-100 text-red-600 hover:bg-red-200 font-medium rounded-lg text-sm transition-colors"
//                       >
//                         Delete All Media
//                       </button>
//                     )}
//                   </div>
//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                     {/* Logo Upload */}
//                     <div className="space-y-4">
//                       <div className="space-y-1">
//                         <label className="block text-sm font-medium text-gray-700">
//                           Logo Image
//                         </label>
//                         <div className="flex items-center gap-2">
//                           <input
//                             type="file"
//                             accept="image/*"
//                             onChange={(e) => setLogoFile(e.target.files?.[0])}
//                             className="flex-1 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition"
//                           />
//                           {(logoFile || property.logo_url) && (
//                             <button
//                               type="button"
//                               onClick={handleDeleteLogo}
//                               disabled={isDeleting.logo}
//                               className={`px-3 py-2 text-sm rounded transition whitespace-nowrap ${
//                                 isDeleting.logo
//                                   ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
//                                   : 'bg-red-500 text-white hover:bg-red-600'
//                               }`}
//                             >
//                               {isDeleting.logo ? (
//                                 <span className="flex items-center">
//                                   <svg
//                                     className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
//                                     fill="none"
//                                     viewBox="0 0 24 24"
//                                   >
//                                     <circle
//                                       className="opacity-25"
//                                       cx="12"
//                                       cy="12"
//                                       r="10"
//                                       stroke="currentColor"
//                                       strokeWidth="4"
//                                     ></circle>
//                                     <path
//                                       className="opacity-75"
//                                       fill="currentColor"
//                                       d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
//                                     ></path>
//                                   </svg>
//                                   Deleting...
//                                 </span>
//                               ) : (
//                                 'Delete Logo'
//                               )}
//                             </button>
//                           )}
//                         </div>
//                       </div>
//                       <div className="flex justify-center">
//                         {logoFile ? (
//                           <div className="relative">
//                             <img
//                               src={URL.createObjectURL(logoFile)}
//                               alt="Preview Logo"
//                               className="h-32 rounded-lg shadow-md object-contain"
//                             />
//                             <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded">
//                               New
//                             </div>
//                           </div>
//                         ) : property.logo_url ? (
//                           <div className="relative">
//                             <img
//                               src={property.logo_url}
//                               alt="Logo"
//                               className="h-32 rounded-lg shadow-md object-contain"
//                             />
//                             <div className="absolute top-2 right-2 bg-gray-500 text-white text-xs px-2 py-1 rounded">
//                               Current
//                             </div>
//                           </div>
//                         ) : (
//                           <div className="h-32 w-full border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
//                             <span className="text-gray-400">No logo uploaded</span>
//                           </div>
//                         )}
//                       </div>
//                     </div>

//                     {/* Homepage Upload */}
//                     <div className="space-y-4">
//                       <div className="space-y-1">
//                         <label className="block text-sm font-medium text-gray-700">
//                           Homepage Image
//                         </label>
//                         <div className="flex items-center gap-2">
//                           <input
//                             type="file"
//                             accept="image/*"
//                             onChange={(e) => setHomepageFile(e.target.files?.[0])}
//                             className="flex-1 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition"
//                           />
//                           {(homepageFile || property.homepage_url) && (
//                             <button
//                               type="button"
//                               onClick={handleDeleteHomepageImage}
//                               disabled={isDeleting.homepageImage}
//                               className={`px-3 py-2 text-sm rounded transition whitespace-nowrap ${
//                                 isDeleting.homepageImage
//                                   ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
//                                   : 'bg-red-500 text-white hover:bg-red-600'
//                               }`}
//                             >
//                               {isDeleting.homepageImage ? (
//                                 <span className="flex items-center">
//                                   <svg
//                                     className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
//                                     fill="none"
//                                     viewBox="0 0 24 24"
//                                   >
//                                     <circle
//                                       className="opacity-25"
//                                       cx="12"
//                                       cy="12"
//                                       r="10"
//                                       stroke="currentColor"
//                                       strokeWidth="4"
//                                     ></circle>
//                                     <path
//                                       className="opacity-75"
//                                       fill="currentColor"
//                                       d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
//                                     ></path>
//                                   </svg>
//                                   Deleting...
//                                 </span>
//                               ) : (
//                                 'Delete Image'
//                               )}
//                             </button>
//                           )}
//                         </div>
//                       </div>
//                       <div className="flex justify-center">
//                         {homepageFile ? (
//                           <div className="relative w-full">
//                             <img
//                               src={URL.createObjectURL(homepageFile)}
//                               alt="Preview Homepage"
//                               className="h-48 w-full rounded-lg shadow-md object-cover"
//                             />
//                             <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded">
//                               New
//                             </div>
//                           </div>
//                         ) : property.homepage_url ? (
//                           <div className="relative w-full">
//                             <img
//                               src={property.homepage_url}
//                               alt="Homepage"
//                               className="h-48 w-full rounded-lg shadow-md object-cover"
//                             />
//                             <div className="absolute top-2 right-2 bg-gray-500 text-white text-xs px-2 py-1 rounded">
//                               Current
//                             </div>
//                           </div>
//                         ) : (
//                           <div className="h-48 w-full border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
//                             <span className="text-gray-400">No homepage image uploaded</span>
//                           </div>
//                         )}
//                       </div>
//                     </div>

//                     {/* Video Upload */}
//                     <div className="space-y-4 md:col-span-2">
//                       <div className="space-y-1">
//                         <label className="block text-sm font-medium text-gray-700">
//                           Property Video
//                         </label>
//                         <div className="flex items-center gap-2">
//                           <input
//                             type="file"
//                             accept="video/*"
//                             onChange={(e) => setVideoFile(e.target.files?.[0])}
//                             className="flex-1 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition"
//                           />
//                           {(videoFile || property.homepage_video_url) && (
//                             <button
//                               type="button"
//                               onClick={handleDeleteVideo}
//                               disabled={isDeleting.video}
//                               className={`px-3 py-2 text-sm rounded transition whitespace-nowrap ${
//                                 isDeleting.video
//                                   ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
//                                   : 'bg-red-500 text-white hover:bg-red-600'
//                               }`}
//                             >
//                               {isDeleting.video ? (
//                                 <span className="flex items-center">
//                                   <svg
//                                     className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
//                                     fill="none"
//                                     viewBox="0 0 24 24"
//                                   >
//                                     <circle
//                                       className="opacity-25"
//                                       cx="12"
//                                       cy="12"
//                                       r="10"
//                                       stroke="currentColor"
//                                       strokeWidth="4"
//                                     ></circle>
//                                     <path
//                                       className="opacity-75"
//                                       fill="currentColor"
//                                       d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
//                                     ></path>
//                                   </svg>
//                                   Deleting...
//                                 </span>
//                               ) : (
//                                 'Delete Video'
//                               )}
//                             </button>
//                           )}
//                         </div>
//                       </div>
//                       <div className="flex justify-center">
//                         {videoFile ? (
//                           <div className="relative w-full max-w-2xl">
//                             <video controls className="w-full rounded-lg shadow-md">
//                               <source src={URL.createObjectURL(videoFile)} type="video/mp4" />
//                               Your browser does not support the video tag.
//                             </video>
//                             <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded">
//                               New
//                             </div>
//                           </div>
//                         ) : property.homepage_video_url ? (
//                           <div className="relative w-full max-w-xl">
//                             <video controls className="w-full rounded-lg shadow-md">
//                               <source src={property.homepage_video_url} type="video/mp4" />
//                               Your browser does not support the video tag.
//                             </video>
//                             <div className="absolute top-2 right-2 bg-gray-500 text-white text-xs px-2 py-1 rounded">
//                               Current
//                             </div>
//                           </div>
//                         ) : (
//                           <div className="w-full max-w-xl border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center py-16">
//                             <span className="text-gray-400">No video uploaded</span>
//                           </div>
//                         )}
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               )}

//               {/* Submit Button (shown on all tabs) */}
//               <div className="mt-8 pt-6 border-t border-gray-200">
//                 <button
//                   type="submit"
//                   disabled={isSubmitting || hasValidationErrors()}
//                   className={`w-full md:w-auto px-6 py-3 bg-black text-white font-semibold rounded-lg shadow-md hover:text-gray-200 focus:outline-none ${
//                     isSubmitting || hasValidationErrors() ? 'opacity-70 cursor-not-allowed' : ''
//                   }`}
//                 >
//                   {isSubmitting ? (
//                     <span className="flex items-center justify-center">
//                       <svg
//                         className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
//                         xmlns="http://www.w3.org/2000/svg"
//                         fill="none"
//                         viewBox="0 0 24 24"
//                       >
//                         <circle
//                           className="opacity-25"
//                           cx="12"
//                           cy="12"
//                           r="10"
//                           stroke="currentColor"
//                           strokeWidth="4"
//                         ></circle>
//                         <path
//                           className="opacity-75"
//                           fill="currentColor"
//                           d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
//                         ></path>
//                       </svg>
//                       Saving...
//                     </span>
//                   ) : (
//                     ' Save Changes'
//                   )}
//                 </button>
//               </div>
//             </form>
//           ) : (
//             <div className="flex justify-center items-center py-12">
//               <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default PropertySettings;
